import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/query-keys";

export interface UserProfileData {
  id: string;
  user_id: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  bio: string | null;
  website: string | null;
  is_seller: boolean;
  seller_verified: boolean;
  country_code: string | null;
  show_country_on_profile: boolean;
  show_purchase_count: boolean;
  show_reviews_publicly: boolean;
  is_early_founder: boolean;
  created_at: string;
  social_youtube: string | null;
  social_twitter: string | null;
  social_discord: string | null;
  social_tiktok: string | null;
  social_instagram: string | null;
  
}

export interface UserReview {
  id: string;
  rating: number;
  title: string | null;
  content: string | null;
  created_at: string;
  asset: {
    id: string;
    title: string;
    slug: string;
  };
}

export interface UserProfileStats {
  purchaseCount: number;
  reviewCount: number;
  averageRatingGiven: number | null;
  accountAgeMonths: number;
}

export function useUserProfile(username: string) {
  return useQuery({
    queryKey: queryKeys.user.profile(username),
    queryFn: async () => {
      if (!username) throw new Error("Username is required");

      // Fetch profile
      const { data: profile, error: profileError } = await supabase
        .from("public_profiles")
        .select("*")
        .eq("username", username)
        .single();

      if (profileError) throw profileError;

      return profile as UserProfileData;
    },
    enabled: !!username,
  });
}

export function useUserProfileStats(
  profileId: string,
  createdAt: string | null | undefined,
  showPurchaseCount: boolean,
) {
  return useQuery({
    queryKey: queryKeys.user.profileStats(profileId, showPurchaseCount),
    queryFn: async () => {
      if (!profileId) throw new Error("Profile ID is required");

      // Purchases are readable only by the buyer, the seller and admins, so
      // counting them from here gave every visitor 0. The definer RPC returns
      // the number (paid, unrefunded) only for profiles that opted in.
      let purchaseCount = 0;
      if (showPurchaseCount) {
        const { data, error } = await supabase.rpc("public_buyer_purchase_count", {
          p_profile_id: profileId,
        });
        if (error) throw error;
        purchaseCount = Number(data ?? 0);
      }

      // Approved only: this is a public page, and RLS would otherwise hand the
      // profile's owner their own pending and rejected reviews too.
      const { data: reviews, error: reviewsError } = await supabase
        .from("reviews")
        .select("rating")
        .eq("user_id", profileId)
        .eq("status", "approved");

      if (reviewsError) throw reviewsError;

      const reviewCount = reviews?.length || 0;
      const averageRatingGiven =
        reviewCount > 0
          ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviewCount
          : null;

      const created = createdAt ? new Date(createdAt) : new Date();
      const accountAgeMonths = Math.floor(
        (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24 * 30)
      );

      return {
        purchaseCount,
        reviewCount,
        averageRatingGiven,
        accountAgeMonths,
      } as UserProfileStats;
    },
    enabled: !!profileId,
  });
}

export function useUserReviews(profileId: string, showReviews: boolean) {
  return useQuery({
    queryKey: queryKeys.user.reviews(profileId, showReviews),
    queryFn: async () => {
      if (!profileId || !showReviews) return [] as any[];

      const { data, error } = await supabase
        .from("reviews")
        .select(`
          id,
          rating,
          title,
          content,
          created_at,
          asset:assets (
            id,
            title,
            slug
          )
        `)
        .eq("user_id", profileId)
        .eq("status", "approved")
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;

      return (data || []).map((review) => ({
        id: review.id,
        rating: review.rating,
        title: review.title,
        content: review.content,
        created_at: review.created_at,
        asset: Array.isArray(review.asset) ? review.asset[0] : review.asset,
      })) as UserReview[];
    },
    enabled: !!profileId && showReviews,
  });
}
